import React from "react";
import { AnonymousPermitFormProps } from "../../../types/forms/Permits/PermitFormProps";
import useShipRepairsPermitForm from "../../../hooks/useShipRepairsPermitForm";
import PermitHeader from "./PermitHeader";
import { PermitType } from "../../../common/enums";
import DangerAlert from "../../Common/DangerAlert";
import PermitActionBar from "./PermitActionBar";
import DateTimeInput from "../../Common/DateTimeInput";
import RemindMe from "./RemindMe";
import TextInput from "../../Common/TextInput";
import TextArea from "../../Common/TextArea";
import RadioButton from "../../Common/RadioButton";
import AdminComment from "./AdminComment";
import SignatureDisplay from "../../Signatures/SignatureDisplay";
import Button from "../../Common/Button";
import RejectPermitModal from "../RejectPermitModal";
import SignatureModal from "../../Signatures/SignatureModal";
import LoadingSpinner from "../../Common/LoadingSpinner";
import AcceptUnsavedChangesModal from "./AcceptUnsavedChangesModal";
import UpdateWithNotificationModal from "./UpdateWithNotificationModal";
import PositionPicker from "./PositionPicker";

function ShipRepairsPermitForm(props: AnonymousPermitFormProps) {
    const formManager = useShipRepairsPermitForm(props);

    const { formState, storedPermit, department, formStateChangeHandler, isLoading } = formManager;
    const { resources } = props;

    if (isLoading) {
        return <LoadingSpinner />;
    }

    return (
        <>
            <div className="mt-5 mb-5">
                <div className="container">
                    <div className="mb-5">
                        <PermitHeader
                            formState={formState}
                            permitDocument={storedPermit}
                            permitNameResource={resources.get("ShipRepairsPermit")}
                            permitType={PermitType.ShipRepairsPermit}
                            resources={resources}
                        />
                    </div>

                    {!!storedPermit && formManager.showRejectionComment ? (
                        <DangerAlert>{storedPermit.rejectionComment}</DangerAlert>
                    ) : null}

                    {storedPermit ? (
                        <PermitActionBar
                            acceptPermitClickHandler={formManager.acceptButtonClickHandler}
                            copyPermitClickHandler={formManager.copyButtonClickHandler}
                            rejectPermitClickHandler={formManager.rejectPermitClickHandler}
                            renewPermitClickHandler={formManager.renewPermitClickHandlerAsync}
                            department={department}
                            permitDocument={storedPermit}
                            resources={props.resources}
                            requestInProgress={formManager.requestInProgress}
                        />
                    ) : null}

                    <div className="mt-3 mb-3">
                        <h1 className="h6">{resources.get("Conditions0")}</h1>
                        <p>{resources.get("Conditions1")}</p>
                        <h1 className="h6">{resources.get("Conditions2")}</h1>
                        <p>{resources.get("Conditions3")}</p>
                    </div>

                    <form onSubmit={formManager.submitHandler}>
                        <PositionPicker
                            canManagePositions={formManager.canManagePositions}
                            canManagePositionIsActive={formManager.canManagePositions}
                            canManagePolygons={formManager.canManagePositionPolygons}
                            canManageComments={formManager.canManagePositions}
                            onPositionAdded={formManager.addPositionPickerRowHandler}
                            onPositionChange={formManager.updatePositionPickerRowHandler}
                            onPositionRemoved={formManager.removePositionPickerRowHandler}
                            onUpdatePositionsClicked={formManager.updatePositionsClickHandlerAsync}
                            positions={formState.positions}
                            resources={props.resources}
                            showUpdatePositionsButton={formManager.showUpdatePositionsButton}
                            canManageCategories={false}
                            selectedCategories={[]}
                            showActiveStatus={true}
                            showCategories={false}
                        />

                        <div className="row mt-3">
                            <DateTimeInput
                                defaultValue={formState.startDate.value ?? new Date()}
                                id="start-date"
                                label={resources.get("StartDateTime")}
                                onBlur={formManager.startDateHandler.blurHandler}
                                className="col-md-6"
                                errorMessage={formState.startDate.errorMessage}
                                ref={formManager.startDateHandler.ref}
                                readOnly={!formManager.canManageContent}
                            />
                            <DateTimeInput
                                defaultValue={formState.endDate.value ?? new Date()}
                                id="end-date"
                                label={resources.get("EndDateTime")}
                                onBlur={formManager.endDateHandler.blurHandler}
                                className="col-md-6"
                                errorMessage={formState.endDate.errorMessage}
                                minValue={formState.startDate.value}
                                ref={formManager.endDateHandler.ref}
                                readOnly={!formManager.canManageContent}
                            />
                        </div>

                        <div className="mt-3">
                            <RemindMe
                                formState={formState}
                                onChange={formManager.registerFormStateChange}
                                resources={resources}
                                readOnly={!formManager.canManageContent}
                            />
                        </div>

                        <div className="row mt-3">
                            <TextInput
                                value={formState.shipName.value}
                                errorMessage={formState.shipName.errorMessage}
                                inputId="ship-name"
                                labelText={props.resources.get("ShipName")}
                                onChange={(s) => formStateChangeHandler("shipName", s)}
                                containerClassName="col-md-6"
                                readOnly={!formManager.canManageContent}
                            />
                            <TextInput
                                value={formState.callSign.value}
                                errorMessage={formState.callSign.errorMessage}
                                inputId="call-sign"
                                labelText={props.resources.get("CallSign")}
                                onChange={(s) => formStateChangeHandler("callSign", s)}
                                containerClassName="col-md-6"
                                readOnly={!formManager.canManageContent}
                            />
                        </div>

                        <div className="row mt-3">
                            <TextArea
                                value={formState.description.value}
                                errorMessage={formState.description.errorMessage}
                                labelText={props.resources.get("WorkPurposeDescription")}
                                onChange={(s) => formStateChangeHandler("description", s)}
                                textAreaId="description"
                                readOnly={!formManager.canManageContent}
                            />
                        </div>

                        <div className="row mt-3">
                            <TextInput
                                inputId="commander-or-representative__name"
                                containerClassName="col-md-6"
                                value={formState.commanderOrRepresentative.name}
                                errorMessage={formState.commanderOrRepresentative.nameErrorMessage}
                                labelText={resources.get("CommanderOrRepresentative")}
                                onChange={(s) =>
                                    formManager.personFormElementChangeHandler("commanderOrRepresentative", {
                                        ...formState.commanderOrRepresentative,
                                        name: s
                                    })
                                }
                                readOnly={!formManager.canManageContent}
                            />
                            <TextInput
                                inputId="commander-or-representative__phone-number"
                                containerClassName="col-md-6"
                                value={formState.commanderOrRepresentative.phoneNumber}
                                errorMessage={formState.commanderOrRepresentative.phoneNumberErrorMessage}
                                labelText={resources.get("CommanderOrRepresentativePhoneNumber")}
                                onChange={(s) =>
                                    formManager.personFormElementChangeHandler("commanderOrRepresentative", {
                                        ...formState.commanderOrRepresentative,
                                        phoneNumber: s
                                    })
                                }
                                placeHolder="e.g. +46705555555"
                                readOnly={!formManager.canManageContent}
                            />
                        </div>

                        <table className="table table-bordered rounded mt-3">
                            <tbody>
                                <tr>
                                    <td width={"50%"}>{resources.get("WillMajorRepairsBePerformed")}</td>
                                    <td width={"50%"}>
                                        <div className="d-flex">
                                            <RadioButton
                                                checked={formState.includesMajorRepairs.value}
                                                onSelect={() => formStateChangeHandler("includesMajorRepairs", true)}
                                                inputId="includes-major-repairs__yes"
                                                label={resources.get("Yes")}
                                                name="includes-major-repairs"
                                                containerClassName="me-3"
                                                readOnly={!formManager.canManageContent}
                                            />
                                            <RadioButton
                                                checked={!formState.includesMajorRepairs.value}
                                                onSelect={() => formStateChangeHandler("includesMajorRepairs", false)}
                                                inputId="includes-major-repairs__no"
                                                label={resources.get("No")}
                                                name="includes-major-repairs"
                                                readOnly={!formManager.canManageContent}
                                            />
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td width={"50%"}>{resources.get("CanShipBeMovedByOwnMachine")}</td>
                                    <td width={"50%"}>
                                        <div className="d-flex">
                                            <RadioButton
                                                checked={formState.shipCanBeMovedByOwnMachine.value}
                                                onSelect={() =>
                                                    formStateChangeHandler("shipCanBeMovedByOwnMachine", true)
                                                }
                                                inputId="ship-can-be-moved-by-own-machine__yes"
                                                label={resources.get("Yes")}
                                                name="ship-can-be-moved-by-own-machine"
                                                containerClassName="me-3"
                                                readOnly={!formManager.canManageContent}
                                            />
                                            <RadioButton
                                                checked={!formState.shipCanBeMovedByOwnMachine.value}
                                                onSelect={() =>
                                                    formStateChangeHandler("shipCanBeMovedByOwnMachine", false)
                                                }
                                                inputId="ship-can-be-moved-by-own-machine__no"
                                                label={resources.get("No")}
                                                name="ship-can-be-moved-by-own-machine"
                                                readOnly={!formManager.canManageContent}
                                            />
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td width={"50%"}>{resources.get("WillPropellersBeRunning")}**</td>
                                    <td width={"50%"}>
                                        <div className="d-flex">
                                            <RadioButton
                                                checked={formState.includesRunningPropellers.value}
                                                onSelect={() =>
                                                    formStateChangeHandler("includesRunningPropellers", true)
                                                }
                                                inputId="includes-running-propellers__yes"
                                                label={resources.get("Yes")}
                                                name="includes-running-propellers"
                                                containerClassName="me-3"
                                                readOnly={!formManager.canManageContent}
                                            />
                                            <RadioButton
                                                checked={!formState.includesRunningPropellers.value}
                                                onSelect={() =>
                                                    formStateChangeHandler("includesRunningPropellers", false)
                                                }
                                                inputId="includes-running-propellers__no"
                                                label={resources.get("No")}
                                                name="includes-running-propellers"
                                                readOnly={!formManager.canManageContent}
                                            />
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td width={"50%"}>{resources.get("CanShipBeMovedByTowBoat")}</td>
                                    <td width={"50%"}>
                                        <div className="d-flex">
                                            <RadioButton
                                                checked={formState.shipCanBeMovedByTowBoat.value}
                                                onSelect={() => formStateChangeHandler("shipCanBeMovedByTowBoat", true)}
                                                inputId="ship-can-be-moved-by-tow-boat__yes"
                                                label={resources.get("Yes")}
                                                name="ship-can-be-moved-by-tow-boat"
                                                containerClassName="me-3"
                                                readOnly={!formManager.canManageContent}
                                            />
                                            <RadioButton
                                                checked={!formState.shipCanBeMovedByTowBoat.value}
                                                onSelect={() =>
                                                    formStateChangeHandler("shipCanBeMovedByTowBoat", false)
                                                }
                                                inputId="ship-can-be-moved-by-tow-boat__no"
                                                label={resources.get("No")}
                                                name="ship-can-be-moved-by-tow-boat"
                                                readOnly={!formManager.canManageContent}
                                            />
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td width={"50%"}>{resources.get("HasAgreementWithTowBoatCompany")}*</td>
                                    <td width={"50%"}>
                                        <div className="d-flex">
                                            <RadioButton
                                                checked={formState.hasAgreementWithTowBoatCompany.value}
                                                onSelect={() =>
                                                    formStateChangeHandler("hasAgreementWithTowBoatCompany", true)
                                                }
                                                inputId="has-agreement-with-tow-boat-company__yes"
                                                label={resources.get("Yes")}
                                                name="has-agreement-with-tow-boat-company"
                                                containerClassName="me-3"
                                                readOnly={!formManager.canManageContent}
                                            />
                                            <RadioButton
                                                checked={!formState.hasAgreementWithTowBoatCompany.value}
                                                onSelect={() =>
                                                    formStateChangeHandler("hasAgreementWithTowBoatCompany", false)
                                                }
                                                inputId="has-agreement-with-tow-boat-company__no"
                                                label={resources.get("No")}
                                                name="has-agreement-with-tow-boat-company"
                                                readOnly={!formManager.canManageContent}
                                            />
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        <p>* {resources.get("VesselsAtOilHarbourInformation")}</p>

                        <p>
                            ** <span className="me-1">{resources.get("ApplicationLinkInfo")}</span>
                            <a
                                target="_blank"
                                href="https://forms.office.com/Pages/ResponsePage.aspx?id=c1WbJj2rI0S6koTQJXfaiAQ74L5YszdDqfl-ffaRp2ZUMlU5WVFMVFBYVzBDNThYVEVURzkwNUZGSS4u"
                            >
                                Application - Use of propeller or bowthruster at berth
                            </a>
                        </p>

                        <p>{resources.get("WhenWorkingInShipsProductionArea")}</p>

                        <AdminComment
                            formElement={formState.adminComment}
                            onChange={(s) => formStateChangeHandler("adminComment", s)}
                            readonly={!formManager.canManageAdminComment}
                            onUpdateButtonClick={formManager.updateAdminCommentButtonClickHandlerAsync}
                            showUpdateButton={formManager.showUpdateAdminCommentButton}
                            resources={resources}
                            show={formManager.showAdminComment}
                            containerClassName="mt-3"
                        />

                        {formManager.showAnonymousUserEmail ? (
                            <TextInput
                                containerClassName="mt-3 col-md-6"
                                inputId="anonymous-user-email"
                                labelText={resources.get("AnonymousUserEmail")}
                                onChange={(s) => formStateChangeHandler("anonymousUserEmail", s)}
                                value={formState.anonymousUserEmail.value}
                                errorMessage={formState.anonymousUserEmail.errorMessage}
                                readOnly={!formManager.canManageContent}
                            />
                        ) : null}

                        <SignatureDisplay
                            resources={resources}
                            signatureManager={formManager.permitProviderSignatureManager}
                            signatureLabel={resources.get("PermitProvider")}
                            containerClassName="mt-3 mb-4"
                        />

                        <div className="mt-3">
                            {formManager.canCreate ? (
                                <Button type="submit" disabled={formManager.requestInProgress}>
                                    {resources.get("Send")}
                                </Button>
                            ) : formManager.canUpdate ? (
                                <Button type="submit" disabled={formManager.requestInProgress}>
                                    {resources.get("Update")}
                                </Button>
                            ) : null}
                            {formManager.canUpdateWithNotification ? (
                                <Button
                                    type="button"
                                    className="ms-2"
                                    onClick={() => formManager.setShowUpdateWithNotificationModal(true)}
                                >
                                    {resources.get("UpdateWithNotification")}
                                </Button>
                            ) : null}
                        </div>
                    </form>
                </div>
            </div>
            <SignatureModal
                resources={resources}
                SignatureManager={formManager.permitProviderSignatureManager}
                ref={formManager.permitProviderSignatureManager.signaturePadRef}
            />
            <RejectPermitModal
                onHide={() => formManager.setShowRejectPermitModal(false)}
                onReject={formManager.rejectPermitHandlerAsync}
                resources={resources}
                show={formManager.showRejectPermitModal}
            />
            <AcceptUnsavedChangesModal
                show={formManager.showAcceptUnsavedChangesModal}
                onHide={() => formManager.setShowAcceptUnsavedChangesModal(false)}
                onAccept={formManager.unsavedChangesModalAcceptHandlerAsync}
                onUpdate={formManager.unsavedChangesModalUpdateHandlerAsync}
                resources={resources}
            />
            <UpdateWithNotificationModal
                onHide={() => formManager.setShowUpdateWithNotificationModal(false)}
                show={formManager.showUpdateWithNotificationModal}
                onUpdate={formManager.updateWithNotificationHandlerAsync}
                permitDocument={formManager.storedPermit}
                resources={resources}
            />
        </>
    );
}

export default ShipRepairsPermitForm;
